import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { Divider, Body, Stack, Tab, Tabs, Typography, getSpacingToken } from '@optimus-web/core';
import { PortfolioEnterpriseGroupResult } from '@mews-commander/data-access';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { ModalSidePanel } from '@mews-commander/ui';
import { PropertyGroupsDeleteWrapper } from '../PropertyGroupsDeleteWrapper';
import { EditPropertyGroupWrapper } from '../EditPropertyGroupWrapper';
import { PropertyGroupsPropertiesTable } from '../PropertyGroupPropertiesTable';
import { FIELD_NAMES } from '../PropertyGroupDialog/types/Form';

interface PropertyGroupDetailsProps {
    propertyGroup: PortfolioEnterpriseGroupResult | undefined;
    isOpened: boolean;
    onUnmount: () => void;
}

enum PropertyGroupsTabValues {
    Details = 'details',
    Properties = 'properties',
}

const PaddedStack = styled(Stack)`
    padding-bottom: ${getSpacingToken('200')}px;
`;

export const PropertyGroupsDetails = ({ propertyGroup, isOpened, onUnmount }: PropertyGroupDetailsProps) => {
    const [isPanelOpened, setIsPanelOpened] = useState<boolean>(false);

    useEffect(() => {
        setIsPanelOpened(isOpened);
    }, [isOpened]);

    const [activeTab, setActiveTab] = useState<string>(PropertyGroupsTabValues.Details);

    const content = propertyGroup && (
        <PaddedStack spacing="400" vertical>
            <Tabs inline activeTabValue={activeTab} onTabClick={(_, value) => setActiveTab(value)}>
                <Tab
                    inline
                    id={PropertyGroupsTabValues.Details}
                    value={PropertyGroupsTabValues.Details}
                    label={<LocalizedText textKey={TextKey.PropertyGroupDetailsTab} />}
                />
                <Tab
                    inline
                    id={PropertyGroupsTabValues.Properties}
                    value={PropertyGroupsTabValues.Properties}
                    label={<LocalizedText textKey={TextKey.PropertiesNavigation} />}
                />
            </Tabs>

            {activeTab === PropertyGroupsTabValues.Details ? (
                <>
                    <Body data-test-id={FIELD_NAMES.DESCRIPTION}>{propertyGroup.description}</Body>
                    <Stack vertical spacing="200">
                        <Stack spacing="200" itemAlignment="center">
                            <Body variant="secondary">
                                <LocalizedText textKey={TextKey.PropertyAddGroupNameField} />
                            </Body>
                            <Typography
                                data-test-id={`${TextKey.PropertyAddGroupNameField}-value`}
                                textStyle="bodyMedium"
                                as="div"
                            >
                                {propertyGroup.name}
                            </Typography>
                        </Stack>
                        <Divider />
                        <Stack spacing="200" itemAlignment="center">
                            <Body variant="secondary">
                                <LocalizedText textKey={TextKey.Properties} />
                            </Body>
                            <Typography textStyle="bodyMedium" as="div">
                                {propertyGroup.propertyIds.length}
                            </Typography>
                        </Stack>
                    </Stack>
                </>
            ) : (
                <PropertyGroupsPropertiesTable propertyGroup={propertyGroup} />
            )}
        </PaddedStack>
    );

    const handleDeleteSuccess = () => {
        setIsPanelOpened(false);
        onUnmount();
    };

    const headerActions = propertyGroup && (
        <PropertyGroupsDeleteWrapper
            selectedPropertyGroups={[propertyGroup]}
            showText={false}
            onSuccess={handleDeleteSuccess}
        />
    );

    const actions = propertyGroup ? (
        <Stack alignment="end">
            <EditPropertyGroupWrapper selectedPropertyGroup={propertyGroup} />
        </Stack>
    ) : null;

    return (
        <ModalSidePanel
            opened={isPanelOpened}
            title={propertyGroup?.name}
            actions={actions}
            onClose={() => setIsPanelOpened(false)}
            onUnmount={onUnmount}
            content={content}
            width="large"
            headerActions={headerActions}
            key={propertyGroup?.id}
        />
    );
};
